import { useState } from 'react'
import { api } from '../../api/client'
import { useQuery } from '../../api/hooks'
import { ErrorBanner, Loading, Panel, Tag } from '../../components/ui'

interface SettingRow {
  key: string
  category: string
  description?: string | null
  value?: string | null
  isSensitive: boolean
}

export function SettingsPage() {
  const settings = useQuery<SettingRow[]>('/admin/settings')
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<unknown>()

  async function save(key: string) {
    setBusy(key)
    setError(undefined)

    try {
      await api(`/admin/settings/${encodeURIComponent(key)}`, {
        method: 'PUT',
        body: { value: edits[key] },
      })

      setEdits((previous) => {
        const next = { ...previous }
        delete next[key]
        return next
      })

      settings.reload()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(null)
    }
  }

  const categories = [...new Set((settings.data ?? []).map((s) => s.category))]

  return (
    <>
      <ErrorBanner error={error ?? settings.error} onDismiss={() => setError(undefined)} />
      {settings.loading && <Loading />}

      {categories.map((category) => (
        <Panel title={category} key={category} flush>
          <table>
            <thead>
              <tr>
                <th style={{ width: 280 }}>Setting</th>
                <th style={{ width: 240 }}>Value</th>
                <th>What it does</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {settings.data
                ?.filter((setting) => setting.category === category)
                .map((setting) => {
                  const dirty = setting.key in edits
                  const value = dirty ? edits[setting.key] : (setting.value ?? '')

                  return (
                    <tr key={setting.key}>
                      <td className="code">{setting.key}</td>
                      <td>
                        {setting.isSensitive ? (
                          <Tag kind="warn">hidden</Tag>
                        ) : (
                          <input
                            value={value}
                            onChange={(e) =>
                              setEdits((previous) => ({ ...previous, [setting.key]: e.target.value }))
                            }
                          />
                        )}
                      </td>
                      <td className="soft">{setting.description}</td>
                      <td>
                        {dirty && (
                          <button
                            className="primary small"
                            disabled={busy === setting.key}
                            onClick={() => void save(setting.key)}
                          >
                            Save
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </Panel>
      ))}
    </>
  )
}
